import { computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, type UpdateClientProfileRequest, type ChangePasswordRequest, type RequestEmailChangeRequest } from '@shopexperts/shared';
import { AUTH_CHECK_QUERY_KEY } from '@/app/stores/clientAuth';

/**
 * Composable for managing client profile data and operations.
 * Provides fetching, updating profile, changing password, and email change request.
 */
export function useClientProfile() {
  const api = useApi();
  const queryClient = useQueryClient();

  // Query keys
  const PROFILE_QUERY_KEY = ['client', 'profile'] as const;

  // Fetch profile
  const {
    data: profileData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => api.client.profile.getProfile(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Computed profile values for easy access
  const profile = computed(() => profileData.value ?? null);
  const user = computed(() => profileData.value?.user ?? null);
  const client = computed(() => profileData.value?.client ?? null);
  const pendingEmailChange = computed(() => profileData.value?.pending_email_change ?? null);

  // Full name computed
  const fullName = computed(() => {
    if (!user.value) return '';
    return `${user.value.first_name} ${user.value.last_name}`.trim();
  });

  // Initials for avatar
  const initials = computed(() => {
    if (!user.value) return '';
    const first = user.value.first_name?.charAt(0) || '';
    const last = user.value.last_name?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateClientProfileRequest) => 
      api.client.profile.updateProfile({ body: data }),
    onSuccess: (newProfile) => {
      queryClient.setQueryData(PROFILE_QUERY_KEY, newProfile);
      // Invalidate auth check so toolbar profile picture updates
      queryClient.invalidateQueries({ queryKey: AUTH_CHECK_QUERY_KEY });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordRequest) =>
      api.client.profile.changePassword({ body: data }),
  });

  // Request email change mutation
  const requestEmailChangeMutation = useMutation({
    mutationFn: (data: RequestEmailChangeRequest) =>
      api.client.profile.requestEmailChange({ body: data }),
    onSuccess: () => {
      // Refetch profile to get updated pending_email_change
      queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
    },
  });

  // Update profile helper
  async function updateProfile(data: UpdateClientProfileRequest) {
    return updateProfileMutation.mutateAsync(data);
  }

  // Change password helper
  async function changePassword(currentPassword: string, newPassword: string) {
    return changePasswordMutation.mutateAsync({
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // Request email change helper
  async function requestEmailChange(newEmail: string, password: string) {
    return requestEmailChangeMutation.mutateAsync({
      new_email: newEmail,
      password,
    });
  }

  return {
    // Data
    profile,
    user,
    client,
    pendingEmailChange,
    fullName,
    initials,

    // Loading states
    isLoading,
    isUpdating: updateProfileMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isRequestingEmailChange: requestEmailChangeMutation.isPending,

    // Errors
    error,
    updateError: updateProfileMutation.error,
    passwordError: changePasswordMutation.error,
    emailChangeError: requestEmailChangeMutation.error,

    // Actions
    refetch,
    updateProfile,
    changePassword,
    requestEmailChange,
  };
}

